/**
 * Booking Details Page
 * Shows comprehensive information about a specific booking
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  CreditCard,
  CheckCircle,
  User,
} from 'lucide-react';
import { realAPI } from '../api/realEndpoints';
import { formatCurrency } from '../utils/helpers';
import { getUserImageSrc } from '../utils/userImage';
import Card from '../components/Card';
import Button from '../components/Button';
import Chat from '../components/Chat';
import './ContentManagement.css';
import { IMAGE_URL } from '../api/client';

interface BookingAddressInfo {
  id: number;
  street: string;
  area: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  latitude: string | number;
  longitude: string | number;
  type: string;
}

interface CustomerInfo {
  id: number;
  name: string;
  image: string | null;
  uid: string;
  email?: string;
  signInProvider?: string[] | null;
}

interface ProviderInfo {
  id: number;
  name: string;
  image: string | null;
  uid: string;
  email?: string;
  averageRating?: number;
  totalRating?: number;
  signInProvider?: string[] | null;
}

interface CategoryInfo {
  id: number;
  name: string;
}

interface ServicesInfo {
  id: number;
  providerId: number;
  serviceId: number;
  serviceName: string;
  description: string;
  price: string;
  duration?: number;
  serviceImage:
    | string
    | string[]
    | {
        id?: number | string;
        url: string;
      }[];
  createdAt: string;
}

interface BookingPaymentDetails {
  id: number;
  userId: number;
  providerId: number;
  bookingId: number;
  amount: number;
  platformCharge: number;
  chargeId: string;
  paymentStatus: string;
  createdAt: string;
  cancellationCharge?: number | string | null;
  bookingCancellationCharge?: number | string | null;
}

interface BookingDetail {
  id: number;
  userId: number;
  providerId: number;
  categoryId: number;
  serviceId: number;
  bookingDate: string;
  slot: string;
  type?: string;
  amount?: number | string | null;
  serviceName?: string | null;
  status: string;
  paymentStatus?: string;
  description: string | null;
  createdAt: string;
  completeAt: string | null;
  bookingAddressInfo: BookingAddressInfo;
  customerInfo: CustomerInfo;
  providerInfo: ProviderInfo;
  categoryInfo: CategoryInfo;
  servicesInfo?: ServicesInfo | null;
  bookingPaymentDetails?: BookingPaymentDetails | null;
  cancellationCharge?: number | string | null;
  bookingCancellationCharge?: number | string | null;
  ratingInfo?: {
    rating: number;
    review?: string;
    createdAt: string;
  } | null;
}

const getBookingStatusClasses = (status: string) => {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'completed') return 'bg-green-100 text-green-700';

  if (normalized === 'accepted') return 'bg-indigo-100 text-indigo-700';

  if (normalized === 'pending') return 'bg-amber-100 text-amber-700';

  if (normalized === 'pending_approval') return 'bg-blue-100 text-blue-700';

  if (normalized === 'cancelled') return 'bg-red-100 text-red-700';

  if (normalized === 'rejected') return 'bg-orange-100 text-orange-700';

  return 'bg-gray-100 text-gray-700';
};

const getPaymentStatusClasses = (status?: string) => {
  const normalized = String(status || '').toLowerCase();

  if (['succeeded', 'paid', 'completed'].includes(normalized)) return 'bg-green-100 text-green-700';

  if (normalized === 'pending') return 'bg-amber-100 text-amber-700';

  if (normalized === 'processing') return 'bg-blue-100 text-blue-700';

  if (normalized === 'transfer') return 'bg-indigo-100 text-indigo-700';

  if (normalized === 'refunded') return 'bg-purple-100 text-purple-700';

  if (normalized === 'failed') return 'bg-red-100 text-red-700';

  if (normalized === 'cancelled') return 'bg-gray-200 text-gray-700';

  return 'bg-gray-100 text-gray-700';
};



const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeServiceImages = (
  value:
    | string
    | string[]
    | {
        id?: number | string;
        url: string;
      }[]
    | null
    | undefined
): string[] => {
  if (!value) return [];

  const toMediaUrl = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) return trimmed;
    if (!IMAGE_URL) return trimmed;
    const base = IMAGE_URL.endsWith('/') ? IMAGE_URL.slice(0, -1) : IMAGE_URL;
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${base}${path}`;
  };

  if (Array.isArray(value)) {
    return value
      .map((img) => {
        if (typeof img === 'string') return toMediaUrl(img);
        if (img && typeof img === 'object' && 'url' in img && typeof img.url === 'string') {
          return toMediaUrl(img.url);
        }
        return '';
      })
      .filter(Boolean)
      .map((img) => String(img));
  }

  const url = toMediaUrl(value);
  return url ? [url] : [];
};

const parseAmount = (value?: number | string | null) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

export default function BookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [currentServiceImageIndex, setCurrentServiceImageIndex] = useState(0);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const data = await realAPI.bookings.getBookingDetails(id);
        setBooking(data);
      } catch (err) {
        console.error('Failed to fetch booking details:', err);
        setError('Failed to load booking details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id]);

  useEffect(() => {
    setCurrentServiceImageIndex(0);
  }, [booking?.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loader mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/bookings')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Bookings
          </Button>
        </div>
        <Card>
          <div className="text-center py-12">
            <p className="text-red-600">{error || 'Booking not found'}</p>
          </div>
        </Card>
      </div>
    );
  }

  const providerImageSrc = getUserImageSrc(
    booking.providerInfo?.image,
    booking.providerInfo?.signInProvider
  );
  const customerImageSrc = getUserImageSrc(
    booking.customerInfo?.image,
    booking.customerInfo?.signInProvider
  );

  const isRequestBooking = booking.type === 'request';
  const displayServiceName = isRequestBooking
    ? booking.serviceName || booking.servicesInfo?.serviceName || 'N/A'
    : booking.servicesInfo?.serviceName || booking.serviceName || 'N/A';
  const displayAmount = isRequestBooking
    ? parseAmount(booking.amount ?? booking.servicesInfo?.price ?? null)
    : parseAmount(booking.servicesInfo?.price ?? booking.amount ?? null);
  const serviceImages = isRequestBooking
    ? providerImageSrc
      ? [providerImageSrc]
      : []
    : normalizeServiceImages(booking.servicesInfo?.serviceImage);
  const serviceImage = serviceImages[currentServiceImageIndex] || null;
  const hasMultipleServiceImages = serviceImages.length > 1;

  const paymentInfo = booking.bookingPaymentDetails;
  const effectivePaymentStatus = paymentInfo?.paymentStatus || booking.paymentStatus || 'unknown';
  const senderId = paymentInfo?.userId || booking.userId;
  const receiverId = paymentInfo?.providerId || booking.providerId;
  const senderName =
    senderId === booking.customerInfo?.id
      ? booking.customerInfo?.name
      : senderId === booking.providerInfo?.id
        ? booking.providerInfo?.name
        : `User #${senderId}`;
  const receiverName =
    receiverId === booking.providerInfo?.id
      ? booking.providerInfo?.name
      : receiverId === booking.customerInfo?.id
        ? booking.customerInfo?.name
        : `User #${receiverId}`;

  const isCancelledRefunded =
    booking.status === 'cancelled' && String(effectivePaymentStatus).toLowerCase() === 'refunded';

  return (
    <div className="dark:bg-gray-900">
      <div className="mb-6" style={{ marginLeft: '18px' }}>
        <button
          onClick={() => navigate('/bookings')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Bookings
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Booking Details</h1>
          <button
            onClick={() => setShowChat(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            View Chat
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-4">
            <Card className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 dark:text-white">
                Service Name
              </h3>
              <div className="space-y-3">
                <div className="relative group w-fit">
                  {serviceImage ? (
                    <>
                      <img
                        src={serviceImage}
                        alt={displayServiceName}
                        className="w-52 h-52 max-w-full rounded-lg object-cover cursor-pointer"
                        onClick={() => setIsImageModalOpen(true)}
                      />
                      <div
                        className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg flex items-center justify-center transition-all cursor-pointer"
                        onClick={() => setIsImageModalOpen(true)}
                      >
                        <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </>
                  ) : (
                    <div className="w-52 h-52 max-w-full rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                      No Image
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 dark:text-white text-lg">
                    {displayServiceName}
                  </p>

                  <span className="text-sm font-semibold bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                    {booking.categoryInfo?.name || 'N/A'}
                  </span>
                </div>
                <p className="text-green-600 font-bold text-lg">
                  {formatCurrency(displayAmount)}
                </p>
                {!isRequestBooking && booking.servicesInfo?.duration ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Duration: {booking.servicesInfo.duration} mins
                  </p>
                ) : null}
                {(isRequestBooking ? booking.description : booking.servicesInfo?.description) ? (
                  <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-md border border-gray-100 dark:border-gray-700">
                    {isRequestBooking
                      ? booking.description
                      : booking.servicesInfo?.description}
                  </div>
                ) : null}
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 dark:text-white">
                Booking Address Information
              </h3>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-500 mt-0.5" />

                <div className="text-sm text-gray-700 dark:text-gray-300 min-w-0 w-full">
                  {/* Street */}
                  {booking.bookingAddressInfo?.street && (
                    <p className="font-medium text-gray-900 dark:text-white break-words">
                      {booking.bookingAddressInfo.street}
                    </p>
                  )}

                  {/* Area */}
                  {booking.bookingAddressInfo?.area && (
                    <div className="max-h-24 overflow-y-auto pr-1">
                      <p
                        className="break-words whitespace-normal"
                        style={{ overflowWrap: 'anywhere' }}
                        title={booking.bookingAddressInfo.area}
                      >
                        {booking.bookingAddressInfo.area}
                      </p>
                    </div>
                  )}

                  {/* City + State */}
                  {(booking.bookingAddressInfo?.city || booking.bookingAddressInfo?.state) && (
                    <p className="break-words">
                      {[booking.bookingAddressInfo?.city, booking.bookingAddressInfo?.state]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}

                  {/* Country + Zipcode */}
                  {(booking.bookingAddressInfo?.country || booking.bookingAddressInfo?.zipcode) && (
                    <p className="break-words">
                      {[booking.bookingAddressInfo?.country, booking.bookingAddressInfo?.zipcode]
                        .filter(Boolean)
                        .join(' - ')}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </div>

          <Card className="p-4 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-white">
              Booking Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <h5 className="text-m text-black-500 font-bold">Booking Status</h5>
                  <span
                    className={`inline-flex mt-1 px-2 py-1 text-xs font-semibold rounded-md capitalize ${getBookingStatusClasses(
                      booking.status
                    )}`}
                  >
                    {booking.status === 'pending_approval'
                      ? 'Pending Approval'
                      : String(booking.status || '').replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-indigo-500 mt-0.5" />
                <div>
                  <h5 className="text-md text-black-500 font-bold">Payment Status</h5>

                  <span
                    className={`inline-flex mt-1 px-2 py-1 text-xs font-semibold rounded-md capitalize ${getPaymentStatusClasses(
                      effectivePaymentStatus
                    )}`}
                  >
                    {String(effectivePaymentStatus).replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-purple-500 mt-0.5" />
                <div>
                  <h5 className="text-md text-black-500 font-bold">Scheduled</h5>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {booking.bookingDate || 'N/A'} | {booking.slot || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-emerald-500 mt-0.5" />
                <div>
                  <h5 className="text-md text-black-500 font-bold">Created At</h5>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDateTime(booking.createdAt)}
                  </p>
                </div>
              </div>

              {booking.completeAt ? (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <h5 className="text-md text-black-500 font-bold">Completed At</h5>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatDateTime(booking.completeAt)}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-100 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Customer
                </h4>
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-white dark:hover:bg-gray-700 p-2 rounded-md"
                  onClick={() => navigate(`/customers/${booking.customerInfo?.id}`)}
                >
                  {customerImageSrc ? (
                    <img
                      src={customerImageSrc}
                      className="w-12 h-12 rounded-full object-cover"
                      alt={booking.customerInfo?.name}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                      {booking.customerInfo?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {booking.customerInfo?.name || 'N/A'}
                    </p>
                    <p className="text-xs text-black-500">{booking.customerInfo?.email || 'N/A'}</p>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-100 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Service Provider
                </h4>
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-white dark:hover:bg-gray-700 p-2 rounded-md"
                  onClick={() => navigate(`/providers/${booking.providerInfo?.id}`)}
                >
                  {providerImageSrc ? (
                    <img
                      src={providerImageSrc}
                      className="w-12 h-12 rounded-full object-cover"
                      alt={booking.providerInfo?.name}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center text-white font-semibold">
                      {booking.providerInfo?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {booking.providerInfo?.name || 'N/A'}
                    </p>
                    <p className="text-xs text-black-500">{booking.providerInfo?.email || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-100 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Payment Details
              </h4>

              {paymentInfo ? (
                <div className="space-y-3 text-sm">
                  {/* Sender & Receiver Card */}
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-100 dark:border-gray-700">
                    <div
                      className={`grid grid-cols-1 ${!isCancelledRefunded ? 'md:grid-cols-2' : ''} gap-3`}
                    >
                      {/* Sender */}
                      <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900">
                          <User className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                        </div>

                        <div className="min-w-0">
                          <p className="text-sm text-blue-500 font-bold">Sender (Customer)</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                            {senderName || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {booking.customerInfo?.email || 'N/A'}
                          </p>
                        </div>
                      </div>

                      {/* Receiver */}
                      {!isCancelledRefunded && (
                        <div className="flex items-start gap-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900">
                            <User className="w-5 h-5 text-purple-600 dark:text-purple-300" />
                          </div>

                          <div className="min-w-0">
                            <p className="text-sm text-purple-500 font-bold">Receiver (Provider)</p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                              {receiverName || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {booking.providerInfo?.email || 'N/A'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Transaction ID */}
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600 dark:text-gray-400">Transaction ID</span>
                    <span className="font-medium text-gray-900 dark:text-white break-all text-right">
                      {paymentInfo.chargeId || 'N/A'}
                    </span>
                  </div>

                  {/* Amount */}
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600 dark:text-gray-400">Amount</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(Number(paymentInfo.amount || 0))}
                    </span>
                  </div>

                  {/* Platform Charge */}
                  {!isCancelledRefunded && (
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-600 dark:text-gray-400">Platform Charge</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(Number(paymentInfo.platformCharge || 0))}
                      </span>
                    </div>
                  )}

                  {/* Cancellation Charge */}
                  {isCancelledRefunded && Number(paymentInfo.cancellationCharge || 0) > 0 && (
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-600 dark:text-gray-400">Cancellation Charge</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        {formatCurrency(Number(paymentInfo.cancellationCharge))}
                      </span>
                    </div>
                  )}

                  {/* Service Provider Earned OR Payment Status */}
                  {!isCancelledRefunded ? (
                    <div className="flex justify-between gap-4 border-t pt-3 mt-2">
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        Service Provider Earned
                      </span>
                      <span className="font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(
                          Number(paymentInfo.amount || 0) - Number(paymentInfo.platformCharge || 0)
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between gap-4 items-center border-t pt-3 mt-2">
                      <span className="text-gray-600 dark:text-gray-400">Payment Status</span>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${getPaymentStatusClasses(
                          paymentInfo.paymentStatus
                        )}`}
                      >
                        {String(paymentInfo.paymentStatus || 'unknown').replace('_', ' ')}
                      </span>
                    </div>
                  )}

                  {/* Created At */}
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600 dark:text-gray-400">Created At</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDateTime(paymentInfo.createdAt)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <p className="text-gray-600 dark:text-gray-300">
                    Detailed payment record not available.
                  </p>

                  <div className="flex justify-between gap-4 items-center">
                    <span className="text-gray-500">Payment Status</span>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${getPaymentStatusClasses(
                        booking.paymentStatus
                      )}`}
                    >
                      {String(booking.paymentStatus || 'unknown').replace('_', ' ')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {booking.ratingInfo ? (
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 dark:text-white">
              Customer Rating
            </h3>
            <div className="flex items-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  className={`w-5 h-5 ${i < (booking.ratingInfo?.rating || 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {booking.ratingInfo?.rating || 0}/5
              </span>
            </div>
            {booking.ratingInfo?.review ? (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {booking.ratingInfo.review}
              </p>
            ) : null}
          </Card>
        ) : null}
      </div>

      {isImageModalOpen && serviceImage ? (
        <div
          className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl flex flex-col items-center gap-3"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
              onClick={() => setIsImageModalOpen(false)}
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={serviceImage}
              alt={displayServiceName}
              className="w-full max-h-[72vh] object-contain rounded-lg bg-white"
            />
            {hasMultipleServiceImages ? (
              <div className="flex items-center gap-2 bg-white/95 rounded-md px-3 py-2">
                <button
                  type="button"
                  className="p-1 rounded border border-gray-300 hover:bg-gray-100"
                  onClick={() =>
                    setCurrentServiceImageIndex((prev) =>
                      prev === 0 ? serviceImages.length - 1 : prev - 1
                    )
                  }
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-700">
                  {currentServiceImageIndex + 1} / {serviceImages.length}
                </span>
                <button
                  type="button"
                  className="p-1 rounded border border-gray-300 hover:bg-gray-100"
                  onClick={() =>
                    setCurrentServiceImageIndex((prev) =>
                      prev === serviceImages.length - 1 ? 0 : prev + 1
                    )
                  }
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {showChat && booking && (
        <Chat
          chatId={booking.id.toString()}
          bookingId={booking.id}
          currentUser={{
            uid: booking.customerInfo?.uid?.toString() || '',
            name: booking.customerInfo?.name || 'Customer',
          }}
          otherUser={{
            uid: booking.providerInfo?.uid?.toString() || '',
            name: booking.providerInfo?.name || 'Provider',
          }}
          onClose={() => setShowChat(false)}
        />
      )}
    </div>
  );
}
